import React from "react";
import {
  View,
  ScrollView,
  TouchableOpacity,
  Platform} from "react-native";
import commonStyles from "../../styles/commonStyles";
import AntDesign from "@expo/vector-icons/AntDesign";
import { AgreementResponse } from "../../types/IAgreementType";
import FormattedText from "../../components/FormattedText";
import Toast from "react-native-toast-message";
import * as FileSystem from "expo-file-system/legacy";

import AsyncStorage from "@react-native-async-storage/async-storage";
import { getUserIdToUse } from "../../utils/getUserIdToUse";
import Constants from "expo-constants";
import OverflowTooltipText from "../../components/OverflowTooltipText";

import * as Sharing from "expo-sharing";
import { SafeAreaView } from "react-native-safe-area-context";
import AppText from "../../components/AppText";
import FontAwesome from "@expo/vector-icons/FontAwesome";
type AgreementDetailsProps = {
  route: {
    params: {
      agreement: AgreementResponse;
    };
  };
  navigation: {
    goBack: () => void;
  };
};

const AgreementDetails: React.FC<AgreementDetailsProps> = ({
  route,
  navigation}) => {
  const { agreement } = route.params;

  const saveAndShare = async (fileUri: string, fileName: string) => {
    try {
      if (await Sharing.isAvailableAsync()) {
        await Sharing.shareAsync(fileUri, {
          mimeType: "application/pdf",
          dialogTitle: `Share ${fileName}`});
      } else {
        Toast.show({
          type: "info",
          text1: "File saved locally"});
      }
    } catch (error) {
      console.error("Error saving or sharing file:", error);
      Toast.show({
        type: "error",
        text1: "Error saving or sharing file."});
    }
  };

  const downloadFile = async (fileName: string) => {
    console.log("Initiating download for file:", fileName);
    const bucketName = Constants.expoConfig?.extra?.BUCKET_NAME;
    const baseUrl = Constants.expoConfig?.extra?.API_URL;

    console.log("Base URL:", baseUrl);
    console.log("Bucket Name:", bucketName);

    Toast.show({
      type: "success",
      text1: "Your download started!"});

    const url = `${baseUrl}portal/shareholding/download?fileName=${encodeURIComponent(
      fileName,
    )}&bucketName=${encodeURIComponent(bucketName)}`;

    // const fileInfo = { fileName, bucketName };

    try {
      const token = await AsyncStorage.getItem("userToken");
      const userId = await getUserIdToUse();

      // `fileName` is a full storage key with folders (e.g. "A/B/C/file.pdf").
      // Use only the last segment for the local file — downloadAsync won't
      // create intermediate directories.
      const baseName = fileName.split("/").pop() || fileName;

      // On Android, let the user pick a destination folder up front so we don't
      // download anything if they cancel.
      let androidDirectoryUri: string | undefined;
      if (Platform.OS === "android") {
        const permissions =
          await FileSystem.StorageAccessFramework.requestDirectoryPermissionsAsync();
        if (!permissions.granted) {
          Toast.show({
            type: "error",
            text1: "Permission denied to access the directory."});
          return;
        }
        androidDirectoryUri = permissions.directoryUri;
      }

      // Single download for every platform — straight to a file, never a Blob
      // (RN can't build a Blob from an ArrayBuffer, which is why blob() threw).
      const tempUri = `${FileSystem.cacheDirectory}${baseName}`;
      const download = await FileSystem.downloadAsync(url, tempUri, {
        headers: {
          Authorization: `Bearer ${token}`,
          emulate_partner_id: userId?.toString() || ""}});

      if (download.status !== 200) {
        throw new Error(`Failed to download file. Status: ${download.status}`);
      }

      if (Platform.OS === "android") {
        // Copy the downloaded file into the user-chosen folder via SAF.
        const base64Data = await FileSystem.readAsStringAsync(download.uri, {
          encoding: FileSystem.EncodingType.Base64});
        const fileUri = await FileSystem.StorageAccessFramework.createFileAsync(
          androidDirectoryUri!,
          baseName,
          "application/pdf",
        );
        await FileSystem.writeAsStringAsync(fileUri, base64Data, {
          encoding: FileSystem.EncodingType.Base64});
        console.log("File successfully saved to:", fileUri);
      } else {
        await saveAndShare(download.uri, baseName);
      }

      Toast.show({
        type: "success",
        text1: "File downloaded successfully!"});
    } catch (error) {
      Toast.show({
        type: "error",
        text1: "Download Failed. Unable to download the file."});
    }
  };

  return (
    <SafeAreaView edges={["bottom", "left", "right"]} style={[commonStyles.flex1, commonStyles.screenBottomGap]}>
      <View style={commonStyles.container}>
        <TouchableOpacity
          onPress={() => navigation.goBack()}
          style={commonStyles.subHeaderContainer}
        >
          <AntDesign name="left" size={24} color="black" testID="back-button" />

          <OverflowTooltipText
            testID="entityName"
            text={agreement.entityName}
            style={commonStyles.subHeader}
            onPress={() => navigation.goBack()}
          />
        </TouchableOpacity>
        <ScrollView>
          <View
            style={[
              commonStyles.subContainer,
              commonStyles.mb10,
              commonStyles.p8,
              commonStyles.mt10,
              commonStyles.mlar2,
            ]}
          >
            <View>
              <View style={commonStyles.subDetails}>
                <AppText
                  testID="agreement-ownership"
                  style={commonStyles.subTitle}
                >
                  Ownership
                </AppText>

                <FormattedText
                  testID="ownership"
                  value={agreement.ownership}
                  type="percentage"
                  decimalPlaces={6}
                  style={commonStyles.subValue}
                />
              </View>
              {agreement.operatingAgreementDocUrl && (
                <View style={commonStyles.subDetails}>
                  <AppText
                    testID="operating-agreements"
                    style={commonStyles.subTitle}
                  >
                    Operating Agreement
                  </AppText>
                  <TouchableOpacity
                    testID="operating-agreement-download"
                    onPress={() => {
                      downloadFile(agreement.operatingAgreementDocUrl);
                    }}
                  >
                    <FontAwesome name="file-pdf-o" size={30} color="#cb180f"  style={{ marginTop: 3 }}  />
                  </TouchableOpacity>
                </View>
              )}

              {agreement.subscriptionAgreementDocUrl && (
                <View style={commonStyles.subDetails}>
                  <AppText
                    testID="subscription-agreement"
                    style={commonStyles.subTitle}
                  >
                    Subscription Agreement
                  </AppText>
                  <TouchableOpacity
                    testID="subscription-agreement-download"
                    onPress={() => {
                      downloadFile(agreement.subscriptionAgreementDocUrl);
                    }}
                  >
                    <FontAwesome name="file-pdf-o" size={30} color="#cb180f"  style={{ marginTop: 3 }}  />
                  </TouchableOpacity>
                </View>
              )}

              {agreement.accreditedCertificateDocUrl && (
                <View style={commonStyles.subDetails}>
                  <AppText
                    testID="accredited-certification"
                    style={commonStyles.subTitle}
                  >
                    Accredited Certification
                  </AppText>
                  <TouchableOpacity
                    testID="accredited-certification-download"
                    onPress={() => {
                      downloadFile(agreement.accreditedCertificateDocUrl);
                    }}
                  >
              
                    <FontAwesome name="file-pdf-o" size={30} color="#cb180f"  style={{ marginTop: 3 }}  />

                  </TouchableOpacity>
                </View>
              )}
            </View>
          </View>
        </ScrollView>
      </View>
    </SafeAreaView>
  );
};

export default AgreementDetails;
